import {
  Bell,
  Bookmark,
  Briefcase,
  Coins,
  Crown,
  FileText,
  Gavel,
  Gift,
  Grid2x2,
  Home,
  Images,
  Import,
  LayoutDashboard,
  LifeBuoy,
  MessageSquare,
  Newspaper,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  Sparkles,
  Store,
  Ticket,
  Users,
  Wallet,
  Wand2,
} from "lucide-react";

import type { NavItem } from "./types";

/**
 * THE navigation catalogue — the single declaration site.
 *
 * Before Phase 01 the tree was declared twice by hand (`Sidebar.tsx` sections,
 * `MobileTabBar.tsx` menuGroups) with no shared source, so the two could and
 * did diverge: a route added to one and missed in the other was invisible on
 * that device permanently and silently. Nine routes were in neither.
 *
 * Rules for editing this file:
 *
 *  1. Adding an entry here makes it appear on every surface its `surfaces`
 *     flags name. There is no second list to update.
 *  2. `audience` is NOT declared here. It is derived from `proxy.ts`'s
 *     protected list via `isProtectedPath` (see ./index.ts), so nav filtering
 *     and route protection cannot disagree.
 *  3. Every route in the app must appear here with a `disposition`, even if it
 *     renders in no navigation surface at all. `contextual` and `hidden` are
 *     valid answers; silence is not.
 *  4. Presentation stays in the components. This file carries identity, not
 *     styling — the one exception is `icon`, which is icon *identity*, and
 *     follows the pattern `createActions.tsx` already established.
 *  5. WHAT `footer: true` MEANS. The footer is the site's public index: every
 *     PUBLIC entry whose disposition is `primary` belongs on it, plus the
 *     legal and support pages that live there alone. It had drifted to seven
 *     links while the product grew to a dozen public destinations — Drops,
 *     Galleries, Artists, Auctions and Special Collections were all reachable
 *     from the sidebar and from nowhere a search engine or a first-time
 *     visitor would look. `footer-coverage.test.ts` now enforces the rule, so
 *     a public destination added tomorrow cannot quietly miss it.
 *
 *     Protected entries are NOT covered by that rule: a guest never sees them
 *     (they are filtered by audience), so they add nothing to the public index
 *     and only the ones that earn a place — Dashboard, Ticket — carry the flag.
 */
export const NAV_CATALOGUE: NavItem[] = [
  // ---------------------------------------------------------------------
  // General
  // ---------------------------------------------------------------------
  {
    // F92: the nav labelled "/" as "Dashboard" while a real, protected
    // /dashboard route existed and was reachable only by post-login redirect.
    // The label is corrected here (nav target only — the page itself is Q3 /
    // Phase 03) and /dashboard gets its own authenticated entry below.
    id: "home",
    path: "/",
    icon: Home,
    group: "general",
    match: "exact",
    // `showFor: "guest"` on the tab row, not `"all"` — see the dashboard entry
    // below for why the signed-in tab row carries Dashboard in this slot.
    surfaces: { desktop: true, mobileTab: { order: 1, showFor: "guest" }, mobileDrawer: false, account: false, footer: true },
    disposition: "primary",
    // `/` and `/dashboard` both render `DashboardContent`, so for a signed-in
    // viewer these two sidebar entries opened the identical screen under two
    // different names — and `match: "exact"` on `/` plus `match: "family"` on
    // `/dashboard` meant the active state could read as ambiguous too.
    //
    // Guests keep Home: `/dashboard` is protected, so it is already filtered
    // out for them and Home is their only entry.
    supersededWhenAuthenticated: { on: ["desktop", "footer"], by: "dashboard" },
  },
  {
    // THE canonical Dashboard URL. `/` is the PUBLIC home — it stays public,
    // stays indexable and stays the logo's target in both locales — but it is
    // no longer a second name for Dashboard in any navigation surface.
    //
    // The tab-row slot is the last place that duplication survived. Desktop was
    // already handled by `home.supersededWhenAuthenticated`, and the drawer and
    // account list only ever carried `/dashboard`; the phone tab row still put
    // Home (`/`) in slot 1 for signed-in users, so a phone had a Home tab and a
    // Dashboard drawer entry opening the same screen at two URLs, and whichever
    // one the user was not on read as inactive. Taking the slot with
    // `showFor: "auth"` keeps the shipped composition intact — Dashboard ·
    // Market · [create FAB] · Wallet · Menu — and a signed-in phone user still
    // has a home button; it now points at the canonical URL.
    id: "dashboard",
    path: "/dashboard",
    icon: LayoutDashboard,
    group: "general",
    match: "family",
    surfaces: { desktop: true, mobileTab: { order: 1, showFor: "auth" }, mobileDrawer: true, account: true, footer: true },
    disposition: "primary",
  },
  {
    id: "blogs",
    path: "/blogs",
    icon: Newspaper,
    group: "general",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },
  {
    id: "message",
    path: "/message",
    icon: MessageSquare,
    group: "general",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "primary",
  },
  {
    id: "ticket",
    path: "/ticket",
    icon: Ticket,
    group: "support",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },

  // ---------------------------------------------------------------------
  // Marketplace
  // ---------------------------------------------------------------------
  {
    // PHASE 04. The search ROUTE (audit F33) plus the mobile tab-row affordance
    // (F34): search was reachable only through the navbar trigger, which is
    // `hidden md:flex`, or two taps deep inside the phone drawer. It takes a tab
    // slot because a marketplace's search is a destination, and adding it here is
    // what puts it on every surface at once — the drawer keeps its own entry, so
    // nothing was displaced to make room.
    id: "search",
    path: "/search",
    icon: Search,
    group: "marketplace",
    match: "family",
    surfaces: { desktop: false, mobileTab: { order: 3, showFor: "all" }, mobileDrawer: false, account: false, footer: true },
    disposition: "primary",
  },
  {
    id: "market",
    path: "/market",
    icon: Store,
    group: "marketplace",
    match: "family",
    // An NFT detail page lives at /assets/[network]/[contract]/[tokenId] and
    // has no navigation entry of its own — it belongs to the browse section
    // the user reached it from, which is Market.
    familyPaths: ["/assets"],
    // `showFor: "guest"`, not `"all"`: the authenticated tab row's slot 2 is
    // Wallet now (see the wallet entry below) — Market stays reachable for a
    // signed-in phone user via the desktop sidebar, the Quick Menu drawer, and
    // the dashboard, so removing it from this one slot loses no reachability.
    // `showFor: "all"`, not "guest". The market IS the product; a signed-in
    // phone user reaching it only through the menu sheet buries the one
    // destination they open most. Wallet moved to slot 4 (the guest row's
    // Artists slot) to make room, so both rows are still five items including
    // the Menu trigger.
    surfaces: { desktop: true, mobileTab: { order: 2, showFor: "all" }, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },
  {
    id: "collection",
    path: "/collection",
    icon: Grid2x2,
    group: "marketplace",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },
  {
    // Orphan route promoted. A curation surface distinct from /collection,
    // previously reachable only by a back-link from its own detail pages —
    // which is a return path, not an entry point.
    id: "specialCollections",
    path: "/special-collections",
    icon: Crown,
    group: "marketplace",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },
  {
    id: "artists",
    path: "/artists",
    icon: Users,
    group: "marketplace",
    match: "family",
    surfaces: { desktop: true, mobileTab: { order: 4, showFor: "guest" }, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },
  {
    id: "drops",
    path: "/drops",
    icon: Sparkles,
    group: "marketplace",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },
  {
    id: "galleries",
    path: "/galleries",
    icon: Images,
    group: "marketplace",
    match: "family",
    // Mobile tab-row slot removed (was order 5, guest-only, the last/lowest-
    // priority slot in the row): with Search holding slot 3, the guest row
    // was Home · Market · Search · Artists · Galleries · Menu — 6 rendered
    // items, one over the 5-item cap. Still reachable via the desktop
    // sidebar and the Quick Menu drawer (mobileDrawer:true below).
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },
  {
    id: "activeBid",
    path: "/active-bid",
    icon: Gavel,
    group: "marketplace",
    match: "family",
    // A PUBLIC auction detail lives at `/auctions/[slug]` and has no entry of its
    // own, exactly as an NFT detail at `/assets/...` belongs to Market above. Until
    // this mapping existed nothing lit up while reading a public auction, the
    // header back-fallback on a shared auction link went to the home page, and
    // the derived breadcrumb said `Home > <auction>` while the page did emit
    // `Home > Auctions > <auction>` in its own JSON-LD — three surfaces disagreeing
    // about one hierarchy.
    //
    // It does NOT take the /auctions index from `myAuctions` below: that entry is
    // `match: "exact"`, and `resolveActiveId` breaks the resulting depth-9 tie in
    // favour of the exact match. See the note there.
    familyPaths: ["/auctions"],
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: false, footer: true },
    disposition: "primary",
  },

  // ---------------------------------------------------------------------
  // Account — where the confirmed orphan routes land
  // ---------------------------------------------------------------------
  {
    id: "wallet",
    path: "/wallet",
    icon: Wallet,
    group: "account",
    match: "family",
    // Mobile tab-row slot RESTORED at order 2, auth-only, taking Market's old
    // shared slot: the auth row is now Dashboard · Wallet · [create FAB] ·
    // Search · Menu. Market drops out of the auth row (now `showFor: "guest"`
    // on its own entry) rather than being displaced to a sixth slot — it stays
    // reachable on mobile via the desktop sidebar, the dashboard, and the
    // Quick Menu drawer. The guest row is unaffected: this entry's
    // `showFor: "auth"` never renders for a guest viewer.
    surfaces: { desktop: true, mobileTab: { order: 4, showFor: "auth" }, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },
  {
    // Orphan route. Protected, financial, recently shipped, and had NO entry
    // anywhere — including ProfilePopover, verified by reading its link list.
    id: "portfolio",
    path: "/portfolio",
    icon: Briefcase,
    group: "account",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },
  {
    // Orphan route. Protected and carries real money (commissions). Only its
    // /referrals/[id] detail pages were linked, from inside the feature itself.
    id: "referrals",
    path: "/referrals",
    icon: Share2,
    group: "account",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },
  {
    // Orphan route. Six saved entity types, zero inbound links.
    id: "saved",
    path: "/saved",
    icon: Bookmark,
    group: "account",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },
  {
    // Orphan route. EXACT match, deliberately: /auctions is the signed-in "my
    // auctions" index while /auctions/[slug] is a public detail page. A family
    // match here would light this item up while browsing a public auction.
    id: "myAuctions",
    path: "/auctions",
    icon: Gavel,
    group: "account",
    match: "exact",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },
  {
    // Orphan route. Exact-match protected, same reasoning as myAuctions.
    id: "myBids",
    path: "/bids",
    icon: Coins,
    group: "account",
    match: "exact",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },
  {
    // Orphan route. The NotificationPopover already linked here (the one
    // orphan with partial coverage); the page now has a durable entry too.
    id: "notifications",
    path: "/notifications",
    icon: Bell,
    group: "account",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },
  {
    id: "settings",
    path: "/settings",
    icon: Settings,
    group: "account",
    match: "family",
    surfaces: { desktop: true, mobileTab: null, mobileDrawer: true, account: true, footer: false },
    disposition: "account",
  },

  // ---------------------------------------------------------------------
  // Footer — legal and support, at EVERY breakpoint
  // ---------------------------------------------------------------------
  {
    id: "help",
    path: "/help",
    icon: LifeBuoy,
    group: "support",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: true },
    disposition: "footer",
  },
  {
    // F24: previously rendered once, in BottomBar, behind `hidden xl:block`
    // inside a `hidden md:block` bar — unreachable below 1280px and on every
    // phone. The footer carries it at all widths now.
    id: "terms",
    path: "/terms-of-service",
    icon: FileText,
    group: "legal",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: true },
    disposition: "footer",
  },
  {
    id: "privacy",
    path: "/privacy-policy",
    icon: ShieldCheck,
    group: "legal",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: true },
    disposition: "footer",
  },

  // ---------------------------------------------------------------------
  // Contextual — reachable by a real, verified in-product link, but not a
  // standing navigation destination. Recorded so the reachability sweep can
  // account for them rather than reporting them as orphans.
  // ---------------------------------------------------------------------
  {
    id: "currencies",
    path: "/currencies",
    icon: Coins,
    group: "marketplace",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: true },
    disposition: "footer",
    note:
      "A market-data reference rather than a browse destination, so it is a footer entry " +
      "rather than sidebar or drawer navigation. Also reached from the dashboard market " +
      "panel (DashboardContent.tsx:331) and from the currency detail modal.",
  },
  {
    id: "createNft",
    path: "/create-nft",
    icon: Sparkles,
    group: "general",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: false },
    disposition: "contextual",
    note: "Owned by the create menu (createActions.tsx), reachable from the desktop FAB and the mobile create sheet.",
  },
  {
    id: "createNftAi",
    path: "/create-nft/ai",
    icon: Wand2,
    group: "general",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: false },
    disposition: "contextual",
    note: "Create-menu entry, per the Phase 01 specification. Not promoted to primary navigation.",
  },
  {
    id: "importCollection",
    path: "/collection/import",
    icon: Import,
    group: "general",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: false },
    disposition: "contextual",
    note:
      "Create-menu entry (createActions.tsx), alongside Create Collection — the two are the " +
      "same decision from the user's side: mint a new collection here, or bring an " +
      "already-deployed contract in. `/collection/import/[id]` is the request detail, reached " +
      "only from the list on this page. Protected in proxy.ts; not primary navigation because " +
      "it is a one-off submission, not a standing destination.",
  },

  // ---------------------------------------------------------------------
  // Intentionally hidden
  // ---------------------------------------------------------------------
  {
    id: "redeem",
    path: "/redeem",
    icon: Gift,
    group: "account",
    match: "family",
    surfaces: { desktop: false, mobileTab: null, mobileDrawer: false, account: false, footer: false },
    disposition: "hidden",
    note:
      "RECORDED EXCEPTION. Gift-card links arrive by email/SMS carrying a token in the URL; " +
      "there is nothing to navigate to without one, so a standing nav entry would lead every " +
      "user to a form they cannot complete. Direct navigation IS supported and stays protected " +
      "in proxy.ts, which round-trips the token through login via callbackUrl.",
  },
];

/** Group render order, shared by every surface that groups. */
export const NAV_GROUP_ORDER: NavItem["group"][] = [
  "general",
  "marketplace",
  "account",
  "support",
  "legal",
];
